import { P as PredictiveSearch } from './theme-predictive-search-component-CDqCh0zg.js';

const MINIMUM_FULL_SEARCH_LENGTH = 2;

function escapeHTML(value = '') {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function formatMoney(value) {
  const amount = Number(value);
  const moneyFormat =
    document.documentElement.getAttribute('xo-money-with-currency-format') ||
    document.documentElement.getAttribute('xo-money-format') ||
    '{{amount}}';

  const formattedAmount = Number.isFinite(amount)
    ? amount.toLocaleString(document.documentElement.lang || 'en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '0.00';

  return moneyFormat
    .replace('{{amount}}', formattedAmount)
    .replace('{{ amount }}', formattedAmount);
}

function renderProduct(product) {
  const url = escapeHTML(product?.url);
  const image = escapeHTML(product?.image);
  const title = escapeHTML(product?.title);
  const price = escapeHTML(formatMoney(product?.price));

  return `
    <a
      href="${url}"
      data-search-result
      class="xo-predictive-search-base__item focus-inset group*"
    >
      <div class="xo-predictive-search-base__image">
        ${
          image
            ? `<img src="${image}" alt="${title}" loading="lazy">`
            : '<span class="xo-predictive-search-base__image-placeholder"></span>'
        }
      </div>
      <div class="xo-predictive-search-base__wrap">
        <p class="xo-predictive-search-base__title">${title}</p>
        <span class="xo-predictive-search-base__price">${price}</span>
      </div>
    </a>
  `;
}

function renderProducts(results) {
  const products = results?.products || [];
  const query = (results?.searchQuery || '').trim();

  if (products.length === 0) {
    return `
      <div class="clei-search-page-predictive__empty">
        No predictive matches
      </div>
    `;
  }

  const fullResultsAction =
    query.length >= MINIMUM_FULL_SEARCH_LENGTH
      ? `
        <div class="xo-predictive-search-base__bottom">
          <button type="submit" class="xo-predictive-search-base__view-all">
            <span>VIEW ALL</span>
          </button>
        </div>
      `
      : `
        <p class="clei-search-query-hint">
          Enter at least two letters to view all results
        </p>
      `;

  return `
    <div class="xo-predictive-search-base__inner clei-search-page-predictive__inner">
      <div class="xo-predictive-search-base__container">
        <div class="xo-predictive-search-base__product">
          <h3 class="xo-predictive-search-base__heading">Products</h3>
          <div class="xo-predictive-search-base__list">
            ${products.map(renderProduct).join('')}
          </div>
        </div>
        ${fullResultsAction}
      </div>
    </div>
  `;
}

function markShortQuery(form, input) {
  const isShort = input.value.trim().length < MINIMUM_FULL_SEARCH_LENGTH;
  form.classList.toggle('clei-search--short-query', isShort);
  return isShort;
}

function initialiseSearchGuards(root = document) {
  root.querySelectorAll('form[role="search"]').forEach((form) => {
    const input = form.querySelector('input[name="q"][data-predictive-search-input]');

    if (!input || form.dataset.cleiSearchGuardReady === 'true') {
      return;
    }

    form.dataset.cleiSearchGuardReady = 'true';
    markShortQuery(form, input);

    input.addEventListener('input', () => markShortQuery(form, input));
    form.addEventListener('submit', (event) => {
      if (markShortQuery(form, input)) {
        event.preventDefault();
        input.focus();
      }
    });
  });
}

function initialiseSearchPage() {
  const input = document.querySelector('[data-predictive-search-input="page"]');
  const result = document.querySelector('[data-predictive-search-result="page"]');

  if (!input || !result || input.dataset.cleiPredictiveReady === 'true') {
    initialiseSearchGuards();
    return;
  }

  input.dataset.cleiPredictiveReady = 'true';

  const predictiveSearch = new PredictiveSearch({
    selectors: {
      input: '[data-predictive-search-input="page"]',
      reset: '[data-predictive-search-reset="page"]',
      result: '[data-predictive-search-result="page"]',
    },
    PredictiveSearchAPIConfig: {
      search_path: '/search',
      resources: {
        type: ['product'],
        options: {
          unavailable_products: 'last',
          fields: ['title', 'body', 'tag', 'vendor', 'product_type', 'variants.title'],
        },
      },
    },
    resultTemplateFct: renderProducts,
    numberOfResultsTemplateFct: (results) =>
      `${results.products.length} product${results.products.length === 1 ? '' : 's'} found`,
    loadingResultsMessageTemplateFct: () => 'Loading',
    onInputFocus: (nodes) => {
      if (nodes.input.value) {
        predictiveSearch.open();
      }
    },
    onOpen: () => {
      document.documentElement.classList.add('clei-search-page-predictive-open');
    },
    onClose: () => {
      document.documentElement.classList.remove('clei-search-page-predictive-open');
    },
    onBodyMousedown: () => false,
    onInputKeyup: () => true,
    onInputBlur: () => false,
    numberOfResults: 10,
  });

  initialiseSearchGuards();

  const hasServerResults = !document.querySelector('.clei-search-empty');
  const query = new URLSearchParams(window.location.search).get('q')?.trim();

  if (!hasServerResults && query) {
    document.documentElement.classList.add('clei-search-predictive-fallback');
    requestAnimationFrame(() => predictiveSearch._search());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialiseSearchPage, { once: true });
} else {
  initialiseSearchPage();
}

document.addEventListener('shopify:section:load', initialiseSearchPage);
